import React, { useEffect } from 'react';
import {
  DialogTitle,
  DialogContent,
  Typography,
  DialogActions,
  Button,
  DialogContentText,
  TextField,
  Divider,
  Dialog,
  IconButton,
  Icon,
  Icons,
  Stack,
} from '@nextiva/next-ui-components';
import { Controller, useForm } from 'react-hook-form-new';
import { getOTPDialogContentRules } from 'utils/getOTPDialogContentRules';
import { OTPDialogProps } from 'interface/WizardV3';
import { BRAND_REGISTRATION_FIELDS } from '../CampaignDialog/constants';
import { useUpdateEnablementInfo } from '../CampaignDialog/WizardContextV3/useUpdateEnablementInfo';
import { usePostResend2FAEmail } from '../CampaignDialog/WizardContextV3/usePostResend2FAEmail';
import { Body2Typo } from '../CampaignDialog/WizardV3.styled';

export function OTPDialog({
  corpAcctNumber,
  open,
  onClose,
  setDisableButton,
  variant,
  values,
  'data-testid': dataTestId,
}: OTPDialogProps) {
  const {
    handleSubmit,
    control,
    formState: { isValid },
    reset,
  } = useForm({
    mode: 'onChange',
    defaultValues: { value: values },
  });
  const { content, rules } = getOTPDialogContentRules(variant);
  const updateEnablementInfo = useUpdateEnablementInfo();
  const postResend2FAEmail = usePostResend2FAEmail();

  useEffect(() => {
    reset({ value: values });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [values]);

  const onSubmit = (data) => {
    const payload = {};
    if (variant === 'restartEmailVerification') {
      let businessEmailNew = null;
      if (data.value !== values) {
        businessEmailNew = data.value;
      }
      postResend2FAEmail.mutate(
        {
          corpAcctNumber,
          businessEmailNew,
          restartAuthVettingStatus: true,
        },
        {
          onSuccess: () => {
            setDisableButton(true);
            onClose();
          },
        },
      );
    } else if (variant === 'changePhone') {
      payload[BRAND_REGISTRATION_FIELDS.MOBILE_PHONE_NUMBER] = data.value;
      updateEnablementInfo.mutate(payload);
      onClose();
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      data-testid={dataTestId}
      sx={{ '& .MuiDialog-paper': { width: '520px' } }}
    >
      <DialogTitle variant="h5" sx={{ padding: '32px' }}>
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
        >
          <Typography variant="h5" color="#55596B">
            {content.title}
          </Typography>
          <IconButton
            aria-label="close-otp-dialog"
            onClick={onClose}
            data-testid={`${dataTestId}-Title-CloseButton`}
          >
            <Icon icon={Icons.close} size={32} />
          </IconButton>
        </Stack>
      </DialogTitle>
      <DialogContent>
        <Divider sx={{ marginBottom: '34px' }} />
        <DialogContentText sx={{ marginBottom: '24px' }}>
          <Body2Typo>{content.description}</Body2Typo>
        </DialogContentText>
        <Controller
          control={control}
          name="value"
          rules={rules}
          render={({
            field: { onChange, onBlur, value },
            fieldState: { error },
          }) => {
            return (
              <TextField
                label={content.label}
                fullWidth
                required
                value={value}
                onChange={onChange}
                variant="outlined"
                onBlur={onBlur}
                error={!!error}
                helperText={error?.message}
                data-testid={`${dataTestId}-RegistrationInProgress-ResubmitDialog2FAInput`}
              />
            );
          }}
        />
        {variant !== 'changePhone' && (
          <DialogContentText sx={{ marginTop: '24px' }}>
            <Typography variant="caption" color="#55596B">
              <strong>Note:</strong> Submissions using personal email address,
              poorly formatted email address, spam-associated email address,
              distribution list emails (e.g., sales@ or support@) or email
              address from free email domains will be rejected. The email must
              belong to an individual employee of the business.
            </Typography>
          </DialogContentText>
        )}

        <Divider sx={{ marginTop: '34px' }} />
      </DialogContent>
      <DialogActions sx={{ padding: '30px 32px 26px' }}>
        <Button
          variant="contained"
          color="secondary"
          onClick={onClose}
          data-testid={`${dataTestId}-CancelButton`}
          sx={{
            '&.MuiButton-sizeMedium': {
              padding: '12px 18px',
            },
          }}
        >
          Cancel
        </Button>
        <Button
          variant="contained"
          color="primary"
          disabled={!isValid}
          onClick={handleSubmit(onSubmit)}
          data-testid={`${dataTestId}-${content.dataTestButton}`}
          sx={{
            '&.MuiButton-sizeMedium': {
              padding: '12px 16px',
              marginLeft: '16px',
            },
          }}
        >
          {content.buttonText}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
